// Phase 2.3 UX correction — inline per-control Findings panel.
//
// Embeds the canonical Assessment Results finding card (FindingEntry) directly
// under each telecom infrastructure control, instead of a standalone panel.
//
// Persistence path (unchanged from Phase 2.3):
//   reviewer_list_infrastructure_findings(p_assessment_id)
//   reviewer_upsert_infrastructure_finding(...)
//   reviewer_delete_infrastructure_finding(p_assessment_id, p_finding_id)
//
// Findings are stored on assessment_findings, so they also appear in the
// overall Findings tab via reviewer_get_findings.
//
// Scoring-neutral. v1_flat remains the active mode.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shield, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  FindingEntry,
  hasEntryData,
  type FindingData,
} from "@/components/reviewer/FindingEntry";
import { getInfrastructureFindingTemplate } from "@/configs/controlGuidance/infrastructureFindingTemplates";
import { resolveInfrastructureControlDisplay } from "@/configs/controlGuidance/infrastructureControlMetadata";

const sb: any = supabase as any;

interface SavedFinding extends FindingData {
  id: string;
}

interface Props {
  assessmentId: string;
  controlKey: string;
  controlLabel: string;
  /** Optional canonical NIST badge text to show in the FindingEntry header. */
  controlCode?: string;
  /** Telecom control implementation status — drives default risk/severity. */
  controlStatus?: string | null;
  reviewerScore?: number | null;
  disabled?: boolean;
}

// Mirror the Assessment Results risk vocabulary (HIGH / MODERATE / LOW).
// Template strings use "High|Medium|Low|Critical"; normalize to FindingEntry tokens.
function normalizeRiskToken(risk: string | null | undefined): string {
  switch ((risk ?? "").toUpperCase()) {
    case "CRITICAL": return "HIGH"; // Assessment Results has no separate Critical badge.
    case "HIGH": return "HIGH";
    case "MEDIUM":
    case "MODERATE": return "MODERATE";
    case "LOW": return "LOW";
    default: return "UNKNOWN";
  }
}

// Telecom control status → finding severity (matches Assessment Results convention).
//   Not Implemented        → High
//   Partially Implemented  → Medium  (rendered as MODERATE in the badge)
//   Implemented / N/A      → no default finding (Implemented suppressed upstream)
function statusToSeverity(status: string | null | undefined): "Critical" | "High" | "Medium" | "Low" {
  switch ((status ?? "").trim()) {
    case "Not Implemented": return "High";
    case "Partially Implemented": return "Medium";
    default: return "Medium";
  }
}

// Phase 2.8.3 — eligibility helper used to gate auto-create/remove transitions.
const FINDING_ELIGIBLE_STATUSES = new Set(["Partially Implemented", "Not Implemented"]);
function isStatusEligible(status: string | null | undefined): boolean {
  return !!status && FINDING_ELIGIBLE_STATUSES.has(status.trim());
}

// Synthetic score so FindingEntry's deriveRiskLevel matches the chosen severity
// without us having to alter its props contract.
//   <=25 HIGH, <=75 MODERATE, >75 LOW (see FindingEntry.deriveRiskLevel)
function statusToSyntheticScore(status: string | null | undefined): number | null {
  switch ((status ?? "").trim()) {
    case "Not Implemented": return 0;
    case "Partially Implemented": return 50;
    case "Implemented": return 100;
    default: return null;
  }
}

// Map FindingEntry.risk_level → severity for the Phase 2.3 RPC.
function riskToSeverity(risk: string | null | undefined): string {
  switch ((risk ?? "").toUpperCase()) {
    case "HIGH": return "High";
    case "MODERATE": return "Medium";
    case "MEDIUM": return "Medium";
    case "LOW": return "Low";
    case "CRITICAL": return "Critical";
    default: return "High";
  }
}

export const TelecomControlFindings: React.FC<Props> = ({
  assessmentId,
  controlKey,
  controlLabel,
  controlCode,
  controlStatus = null,
  reviewerScore = null,
  disabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [findings, setFindings] = useState<SavedFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Phase 2.8.1 — Resolve display once per control. Reviewer UI must show the
  // human-readable title + canonical NIST badge, never the internal control_key.
  const display = useMemo(
    () => resolveInfrastructureControlDisplay(controlKey, controlLabel),
    [controlKey, controlLabel],
  );
  const badgeCode = controlCode && controlCode.trim() && controlCode !== controlKey
    ? controlCode
    : display.nistBadge;

  const load = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    const { data, error } = await sb.rpc("reviewer_list_infrastructure_findings", {
      p_assessment_id: assessmentId,
    });
    if (!error && Array.isArray(data)) {
      const mine: SavedFinding[] = data
        .filter((r: any) => r.control_key === controlKey)
        .map((r: any) => ({
          id: r.id,
          control_code: badgeCode,
          finding_title: r.finding_title ?? "",
          priority_level: r.priority_level ?? "",
          recommended_action: r.recommended_action ?? "",
          timeframe: r.timeframe ?? "",
          recommended_measures: r.recommended_measures ?? "",
          remediation_path: r.remediation_path ?? "",
          risk_level: normalizeRiskToken(r.risk_level),
          risk_override: false,
          owners: Array.isArray(r.owners) ? r.owners : [],
          due_date: r.due_date ?? null,
        }));
      setFindings(mine);
      if (mine.length > 0) setExpanded(true);
    }
    setLoading(false);
  }, [assessmentId, controlKey, badgeCode]);

  useEffect(() => { void load(); }, [load]);

  const upsert = useCallback(
    async (id: string | null, data: FindingData): Promise<string | null> => {
      setSaving(true);
      const { data: newId, error } = await sb.rpc("reviewer_upsert_infrastructure_finding", {
        p_assessment_id: assessmentId,
        p_finding_id: id,
        p_control_key: controlKey,
        p_severity: riskToSeverity(data.risk_level),
        p_finding_title: data.finding_title,
        p_recommended_action: data.recommended_action,
        p_recommended_measures: data.recommended_measures,
        p_remediation_path: data.remediation_path,
        p_priority_level: data.priority_level,
        p_timeframe: data.timeframe,
        p_risk_level: data.risk_level,
        p_reviewer_comment: null,
        p_owners: (data as any).owners ?? [],
        p_due_date: (data as any).due_date ?? null,
      });
      setSaving(false);
      if (error) {
        console.error("[TelecomControlFindings] upsert error", error);
        return null;
      }
      return (newId as string) ?? id;
    },
    [assessmentId, controlKey],
  );

  // Build a seed FindingData from the control's current status (used by both
  // the manual "+ Add" button and the Phase 2.8.3 auto-create effect).
  const buildSeed = useCallback(
    (status: string | null | undefined): FindingData => {
      const severity = statusToSeverity(status);
      const tpl = getInfrastructureFindingTemplate(controlKey, severity);
      return {
        control_code: badgeCode,
        finding_title: tpl?.finding_title ?? "",
        priority_level: tpl?.priority_level ?? "",
        recommended_action: tpl?.recommended_action ?? "",
        timeframe: tpl?.timeframe ?? "",
        recommended_measures: tpl?.recommended_measures ?? "",
        remediation_path: tpl?.remediation_path ?? "",
        risk_level: normalizeRiskToken(tpl?.risk_level ?? severity),
        risk_override: false,
        owners: [],
        due_date: null,
      };
    },
    [controlKey, badgeCode],
  );

  // True when the saved finding fields still match what the template would
  // emit for either eligible severity. Used to decide whether an auto-remove
  // on transition to Implemented / N/A is safe (no user edits to discard).
  const isUnedited = useCallback(
    (f: SavedFinding): boolean => {
      for (const sev of ["High", "Medium"] as const) {
        const tpl = getInfrastructureFindingTemplate(controlKey, sev);
        if (!tpl) continue;
        if (
          f.finding_title === (tpl.finding_title ?? "") &&
          f.priority_level === (tpl.priority_level ?? "") &&
          f.recommended_action === (tpl.recommended_action ?? "") &&
          f.timeframe === (tpl.timeframe ?? "") &&
          f.recommended_measures === (tpl.recommended_measures ?? "") &&
          f.remediation_path === (tpl.remediation_path ?? "")
        ) {
          return true;
        }
      }
      return false;
    },
    [controlKey],
  );

  const handleAddFinding = async () => {
    setExpanded(true);
    const seed = buildSeed(controlStatus);
    const newId = await upsert(null, seed);
    if (newId) setFindings((prev) => [...prev, { ...seed, id: newId }]);
  };

  const handleChange = useCallback(
    (id: string, data: FindingData) => {
      // Optimistic local update + debounced save.
      setFindings((prev) => prev.map((f) => (f.id === id ? { ...data, id } : f)));
      if (!hasEntryData(data)) return;
      // Fire and forget — FindingEntry already debounces by user interaction.
      void upsert(id, data);
    },
    [upsert],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const prev = findings;
      setFindings((p) => p.filter((f) => f.id !== id));
      const { error } = await sb.rpc("reviewer_delete_infrastructure_finding", {
        p_assessment_id: assessmentId,
        p_finding_id: id,
      });
      if (error) {
        console.error("[TelecomControlFindings] delete error", error);
        setFindings(prev);
      }
    },
    [assessmentId, findings],
  );

  // Phase 2.8.3 — Auto-create on transition INTO an eligible status, and
  // auto-remove any unedited template findings on transition OUT.
  //
  // Mirrors Assessment Results behavior: the finding shows up the moment the
  // reviewer saves a Partial/Not Implemented status, and disappears (without
  // a confirmation prompt) when they revert to Implemented / N/A — but only
  // for findings that still match the template (i.e., never authored over).
  // User-edited findings are preserved and remain visible in the Findings tab.
  const prevStatusRef = useRef<string | null | undefined>(undefined);
  const autoSeededRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (loading || disabled) return;
    const prev = prevStatusRef.current;
    const curr = controlStatus;
    // Skip the very first observation — never auto-act on initial mount.
    if (prev === undefined) {
      prevStatusRef.current = curr;
      return;
    }
    if (prev === curr) return;
    prevStatusRef.current = curr;

    const wasEligible = isStatusEligible(prev);
    const nowEligible = isStatusEligible(curr);

    if (!wasEligible && nowEligible && findings.length === 0) {
      // Auto-seed exactly one finding from the template.
      (async () => {
        const seed = buildSeed(curr);
        const newId = await upsert(null, seed);
        if (newId) {
          autoSeededRef.current.add(newId);
          setFindings((p) => [...p, { ...seed, id: newId }]);
          setExpanded(true);
        }
      })();
      return;
    }

    if (wasEligible && !nowEligible && findings.length > 0) {
      // Auto-remove only unedited (template-matching) findings.
      const toRemove = findings.filter((f) => isUnedited(f));
      if (toRemove.length === 0) return;
      void (async () => {
        for (const f of toRemove) {
          await sb.rpc("reviewer_delete_infrastructure_finding", {
            p_assessment_id: assessmentId,
            p_finding_id: f.id,
          });
          autoSeededRef.current.delete(f.id);
        }
        setFindings((p) => p.filter((f) => !toRemove.some((r) => r.id === f.id)));
      })();
    }
  }, [controlStatus, loading, disabled, findings, buildSeed, upsert, isUnedited, assessmentId]);

  const eligible = isStatusEligible(controlStatus);
  const findingCount = findings.length;

  // When the control is not in an eligible status and there are no preserved
  // (user-edited) findings to surface, render nothing — parity with the
  // pre-Phase-2.8.3 conditional mount.
  if (!eligible && findingCount === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-foreground hover:bg-muted/50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Findings</span>
          {findingCount > 0 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {findingCount}
            </Badge>
          )}
          {saving && <span className="text-[10px] text-muted-foreground">Saving…</span>}
          {!eligible && findingCount > 0 && (
            <span className="text-[10px] text-muted-foreground italic">
              preserved (status no longer requires finding)
            </span>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">{expanded ? "Collapse" : "Expand"}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
          {loading && (
            <div className="text-xs text-muted-foreground italic">Loading findings…</div>
          )}

          {findings.map((f) => (
            <FindingEntry
              key={f.id}
              controlCode={badgeCode}
              controlTitle={display.title}
              reviewerScore={reviewerScore ?? statusToSyntheticScore(controlStatus)}
              existingFinding={f}
              disabled={disabled}
              onDelete={() => handleDelete(f.id)}
              onChange={(data) => handleChange(f.id, data)}
              defaultExpanded={findings.length === 1}
            />
          ))}

          {/* "Add another finding" button removed — telecom controls have a
              single template, so the auto-seeded finding is the only one
              available. Re-enable only if additional templates per control
              are introduced. */}

          {eligible && findingCount === 0 && !loading && (
            <p className="text-center text-xs text-muted-foreground italic">
              Auto-creating finding from template…
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TelecomControlFindings;
